import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import ProjectModal from '../components/ProjectModal';
import { ArrowRight } from 'lucide-react';
import { portfolio } from '../data/mock';
import ImageRevealOnScroll from '../components/ImageRevealOnScroll';

const PortfolioPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'infrastructure', name: 'Infrastructure' },
    { id: 'telecom', name: 'Telecommunications' },
    { id: 'utilities', name: 'Utilities' },
    { id: 'roads', name: 'Roads & Civil' },
    { id: 'commercial', name: 'Commercial' }
  ];

  const filteredProjects = selectedCategory === 'all'
    ? portfolio
    : portfolio.filter(p => p.category === selectedCategory);

  const openProjectModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const closeProjectModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedProject(null), 300);
  };

  return (
    <div className="min-h-screen pt-32 pb-24">
      {/* Page Header */}
      <section className="px-8 mb-20">
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-sm text-gray-500 uppercase tracking-widest mb-4 font-semibold">
            Our Work
          </div>
          <h1 className="text-6xl md:text-7xl font-bold mb-6 tracking-tight text-white">
            Portfolio
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Major infrastructure and commercial projects delivered with strategic leadership and excellence
          </p>
        </div>
      </section>

      {/* WOW: Dramatic Image Reveal - Hero Projects */}
      <section className="px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Featured Projects
            </h2>
            <p className="text-gray-400">
              Transforming Australia's infrastructure landscape
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <ImageRevealOnScroll
              src="https://images.unsplash.com/photo-1761025198913-a75dc7699a63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxpbmZyYXN0cnVjdHVyZSUyMGNvbnN0cnVjdGlvbiUyMHByb2plY3QlMjBhZXJpYWx8ZW58MHx8fHwxNzczMDQzMzk4fDA&ixlib=rb-4.1.0&q=85"
              alt="Major infrastructure project"
              direction="left"
            />
            <ImageRevealOnScroll
              src="https://images.unsplash.com/photo-1772617661549-98d2ae35ab2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwYnVpbGRpbmclMjBjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3MzA0MzM5OXww&ixlib=rb-4.1.0&q=85"
              alt="Commercial development project"
              direction="right"
            />
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-8 py-4 rounded-sm font-semibold transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-white text-navy-dark'
                    : 'bg-white bg-opacity-5 text-gray-400 hover:bg-opacity-10 hover:text-white'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <Card
                key={project.id}
                className="bg-navy-card border-navy-border hover-lift-enhanced group cursor-pointer overflow-hidden project-card"
                onClick={() => openProjectModal(project)}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <CardContent className="p-0">
                  <div className="p-8">
                    <div className="flex justify-between items-start mb-5">
                      <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold bg-white bg-opacity-5 px-3 py-1 rounded-sm">
                        {project.sector}
                      </span>
                      <span className="text-lg font-bold text-white">{project.value}</span>
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-2 text-white group-hover:text-gray-100 transition-colors">
                      {project.title}
                    </h3>
                    
                    {project.subtitle && (
                      <p className="text-sm text-gray-500 mb-4">{project.subtitle}</p>
                    )}
                    
                    <p className="text-gray-400 leading-relaxed mb-6 group-hover:text-gray-300 transition-colors line-clamp-3">
                      {project.description}
                    </p>
                    
                    <div className="flex items-center text-white font-semibold group-hover:gap-2 transition-all">
                      <span className="text-sm">View Details</span>
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform" />
                    </div>
                  </div>
                  <div className="h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transition-opacity"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Project Modal */}
      <ProjectModal
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={closeProjectModal}
      />
    </div>
  );
};

export default PortfolioPage;
